import React, { useState, useMemo, useEffect } from 'react';
import { Race, RaceAsset } from '../../contexts/BettingProvider';
import RaceLane from './RaceLane';
import { 
  calculateFireIntensity, 
  generatePlacementHistoryFromRaceData,
  calculateTugOfWarBars
} from '../../utils/raceCalculations';
import { TugOfWarBar } from '../../types';

interface RaceTrackProps {
  race: Race;
  getAssetCSSColor: (symbol: string) => string;
  playerPick?: string | null;
  sortedAssetsWithPerformance: Array<RaceAsset & { realTimePerformance: number }>;
  currentRound?: any;
  currentSession?: any;  // Need to check session_status for cashed_out
  isBettingEnabled?: boolean;
  bettingTimeRemaining?: number | null;
  racingTimeRemaining?: number | null;
  betAmount?: number;
  isLoadingSession?: boolean;
  winLossStatus?: 'winning' | 'losing' | 'won' | 'lost' | 'cashed_out' | null;
  matchupHistory?: any[];  // Raw matchup history from backend for tug-of-war display
}

const RaceTrack: React.FC<RaceTrackProps> = ({ 
  race, 
  getAssetCSSColor, 
  playerPick, 
  sortedAssetsWithPerformance, 
  currentRound,
  currentSession,
  isBettingEnabled = true, 
  bettingTimeRemaining, 
  racingTimeRemaining,
  betAmount = 0.01,
  isLoadingSession = false,
  winLossStatus = null,
  matchupHistory = []
}) => {
  const sortedAssets = sortedAssetsWithPerformance;
  
  // Generate placement history from real race winner data
  const placementHistory = useMemo(() => {
    return generatePlacementHistoryFromRaceData(race);
  }, [race]);
  
  // Generate race history with fire intensities for 1v1 winners only
  const fullRaceHistory = useMemo(() => {
    if (!race.assets[0]?.raceWinners || race.assets[0].raceWinners.length === 0) {
      return [];
    }

    const podiumResults = race.assets[0].raceWinners;
    
    // In 1v1 races, only first place matters (winner vs loser, no 2nd/3rd)
    const allFirsts = podiumResults.map(p => p.first).filter(f => f !== null) as Array<{ symbol: string; percentage: number }>;
    const firstIntensities = calculateFireIntensity(allFirsts);
    
    return podiumResults.map((podium, index) => {
      return {
        first: podium.first ? {
          ...podium.first,
          fireIntensity: firstIntensities[index]
        } : null,
        second: null, // Not used in 1v1
        third: null   // Not used in 1v1
      };
    });
  }, [race]);
  
  // Calculate tug-of-war bars from matchup history for betting phase display
  const tugOfWarBars = useMemo((): TugOfWarBar[] => {
    if (!matchupHistory || matchupHistory.length === 0 || race.assets.length < 2) {
      return [];
    }
    
    const assetA = race.assets[0].symbol;
    const assetB = race.assets[1].symbol;
    
    return calculateTugOfWarBars(matchupHistory, assetA, assetB);
  }, [matchupHistory, race.assets]);
  
  const [animationTime, setAnimationTime] = useState(0);
  
  useEffect(() => {
    // Animation timer for idle car movement
    const interval = setInterval(() => {
      setAnimationTime(prev => (prev + 1) % 30);
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  
  if (!race || !race.assets || race.assets.length === 0) {
    return null;
  }
  
  // Get current phase display info
  const getPhaseInfo = () => {
    // ✅ Check if session is being created
    if (isLoadingSession) {
      return {
        icon: '⏳',
        time: '',
        label: 'LOADING RACE',
        isLoading: true
      };
    }
    
    // ✅ Check if we're in demo mode (no current round or demo-race ID)
    const isDemoMode = !currentRound || currentRound.roundId === 'demo-round';
    
    if (isDemoMode) {
      return {
        icon: '🎮',
        time: '',
        label: 'START GAME',
        isLoading: false,
        isDemoMode: true
      };
    }
    
    // Active session - use round phase
    const phase = currentRound?.phase;
    
    if (phase === 'BETTING') {
      // ✅ Show "RACE STARTING" when countdown ends
      if (!bettingTimeRemaining || bettingTimeRemaining < 1) {
        return {
          icon: '🚀',
          time: '',
          label: 'RACE STARTING',
          isLoading: true
        };
      }
      return {
        icon: '⏱',
        time: `${bettingTimeRemaining}s`,
        label: 'BETTING',
        isLoading: false
      };
    } else if (phase === 'RACING') {
      // ✅ Show animated loading when countdown ends
      if (!racingTimeRemaining || racingTimeRemaining < 1) {
        return {
          icon: '⚡',
          time: '',
          label: 'FINISHING',
          isLoading: true
        };
      }
      return {
        icon: '🏁',
        time: `${racingTimeRemaining}s`,
        label: 'RACING',
        isLoading: false
      };
    } else if (phase === 'SETTLEMENT') {
      // Settlement phase - fetching exact prices from Pyth (variable duration)
      return {
        icon: '⚡',
        time: '',
        label: 'CALCULATING',
        isLoading: true
      };
    } else if (phase === 'RESULTS') {
      // Show results based on win/loss or cashed out
      const playerWon = currentRound?.player_won;
      const isCashedOut = currentSession?.session_status === 'cashed_out';
      
      if (isCashedOut) {
        // User cashed out - show different message than regular win
        return {
          icon: '💰',
          time: '',
          label: 'CASHED OUT',
          isLoading: false
        };
      } else if (playerWon) {
        // Regular win (haven't cashed out yet)
        return {
          icon: '🏆',
          time: '',
          label: 'YOU WON!',
          isLoading: false
        };
      } else {
        // Lost
        return {
          icon: '💔',
          time: '',
          label: 'BUSTED',
          isLoading: false
        };
      }
    }
    
    return {
      icon: '⏱',
      time: '',
      label: 'WAITING',
      isLoading: true
    };
  };
  
  const phaseInfo = getPhaseInfo();
  
  return (
    <div className="race-track-container" style={{ position: 'relative' }}>
      {/* Win/Loss Badge Overlay - positioned over VS sign */}
      {winLossStatus && (
        <div 
          className={`win-loss-badge ${winLossStatus === 'cashed_out' ? 'won' : winLossStatus}`}
          style={{
            position: 'absolute',
            top: '20px',
            left: '50%',
            transform: 'translateX(-50%) rotate(-12deg)',
            zIndex: 100
          }}
        >
          {winLossStatus === 'cashed_out' ? 'CASHED OUT' : winLossStatus.toUpperCase()}
        </div>
      )}
      
      {/* Vertical drag race track - cars race upward through space */}
      <div className="vertical-race-track">
        {/* Dark background - cleaner, less distracting */}
        <div className="track-dark-background"></div>
        
        {/* Horizon Glow - Always visible for depth */}
        <div className="horizon-glow"></div>
        
        {/* Side Barriers - 3D Perspective Track Boundaries (always visible) */}
        <div className="side-barrier side-barrier-left"></div>
        <div className="side-barrier side-barrier-right"></div>
        
        {/* Animated Highway Effects - Only during racing, settlement, and results */}
        {(currentRound?.phase === 'RACING' || currentRound?.phase === 'SETTLEMENT' || currentRound?.phase === 'RESULTS') && (
          <>
            {/* Space Dust Streaks - Edge frame effect */}
            <div className="space-dust-container space-dust-left">
              <div className="dust-streak"></div>
              <div className="dust-streak"></div>
              <div className="dust-streak"></div>
              <div className="dust-streak"></div>
              <div className="dust-streak"></div>
            </div>
            <div className="space-dust-container space-dust-right">
              <div className="dust-streak"></div>
              <div className="dust-streak"></div>
              <div className="dust-streak"></div>
              <div className="dust-streak"></div>
              <div className="dust-streak"></div>
            </div>
            
            {/* Star Field - Background particles */}
            <div className="star-field">
              <div className="star star-1"></div>
              <div className="star star-2"></div>
              <div className="star star-3"></div>
              <div className="star star-4"></div>
              <div className="star star-5"></div>
              <div className="star star-6"></div>
              <div className="star star-7"></div>
              <div className="star star-8"></div>
              <div className="star star-9"></div>
              <div className="star star-10"></div>
            </div>
            
            {/* Passing Planets - Occasional drift-by */}
            <div className="passing-planets">
              <div className="planet planet-1"></div>
              <div className="planet planet-2"></div>
              <div className="planet planet-3"></div>
              <div className="planet planet-4"></div>
              <div className="planet planet-5"></div>
            </div>
          </>
        )}
        
        {/* Center divider line - "VS" visual separator (subtle, always visible) */}
        <div className="center-divider"></div>
        
        {/* VS Display - Top of Track */}
        <div className="vs-display">VS</div>
        
        {/* Race Rules Display - Below VS */}
        <div className="race-rules-display">
          <div>15s RACE</div>
          <div>HIGHEST % GAIN WINS</div>
        </div>
        
        {/* Shot Clock Display - Center of Track */}
        <div className="shot-clock-display">
          <div className={`shot-clock-time ${phaseInfo.isLoading ? 'loading-pulse' : ''}`}>
            {phaseInfo.time || phaseInfo.icon}
          </div>
          <div className="shot-clock-phase">{phaseInfo.label}</div>
        </div>
        
        {/* React-based lane generation */}
        {race.assets.map((asset, laneIndex) => {
          const assetSymbol = asset.symbol;
          const isMobile = window.innerWidth <= 768;
          
          const assetWithPerformance = sortedAssets.find(sortedAsset => sortedAsset.symbol === assetSymbol);
          if (!assetWithPerformance) return null;
          
          const performanceRanking = sortedAssets.findIndex(sortedAsset => sortedAsset.symbol === assetSymbol);
          const assetHistory = placementHistory.find(h => h.symbol === asset.symbol);
          
          // Calculate performance gap for dynamic scaling
          const leaderPerformance = sortedAssets[0]?.realTimePerformance || 0;
          const thisPerformance = assetWithPerformance.realTimePerformance;
          const performanceGap = Math.abs(leaderPerformance - thisPerformance);
          
          // VERTICAL RACING: Cars shift position based on performance
          // Background scrolls to create "racing through space" effect
          let verticalPosition;
          
          // ✅ Check if we're in demo mode (no active session)
          const isDemoMode = !currentRound || currentRound.roundId === 'demo-round';
          
          if (isDemoMode) {
            // ✅ DEMO MODE: Show live price movements with smooth animation
            const centerPosition = 50;
            
            // Use moderate scaling for demo (not too dramatic, but noticeable)
            const scaledGap = performanceGap * 25; // 25x multiplier (was 20x) - more visible separation
            const minimumGap = performanceRanking > 0 ? 8 : 0; // Min 8% visual gap (was 3%) - at least one car length
            const dynamicGap = Math.min(Math.max(scaledGap, minimumGap), 30); // Max 30% separation (was 25%)
            
            // Split the gap: leader goes UP, trailer goes DOWN from center
            const halfGap = dynamicGap / 2;
            const positionOffset = performanceRanking === 0 ? halfGap : -halfGap;
            
            // Add subtle idle animation on top of position
            const revAmount = 0.8; // Smaller movement than betting phase
            const movement = Math.sin(animationTime * 0.5 + laneIndex) * revAmount;
            
            verticalPosition = centerPosition + positionOffset + movement;
          } else if (currentRound?.phase === 'SETTLEMENT' || currentRound?.phase === 'RESULTS') {
            // SETTLEMENT/RESULTS: Show final positions based on race results
            const basePosition = 55;
            // Scale with minimum gap so even small differences are visible
            const scaledGap = performanceGap * 20; // 20x multiplier (was 15x)
            const minimumGap = performanceRanking > 0 ? 10 : 0; // Min 10% visual gap for loser (was 5%) - at least one car length
            const dynamicGap = Math.min(Math.max(scaledGap, minimumGap), 25); // Max 25% separation (was 20%)
            const positionOffset = performanceRanking === 0 ? 0 : -dynamicGap;
            verticalPosition = basePosition + positionOffset;
          } else if (currentRound?.phase === 'BETTING') {
            // BETTING: Cars centered, idle with subtle rev animation
            const basePosition = 50; // Center position
            const revAmount = 1.5; // Slightly more movement for visual interest
            const movement = Math.sin(animationTime * 0.8 + laneIndex) * revAmount;
            verticalPosition = basePosition + movement;
          } else if (currentRound?.phase === 'RACING') {
            // RACING: EXTREME DRAMA - massive visual separation!
            const centerPosition = 50; // True center of track
            
            // MAXIMUM DRAMA scaling
            const scaledGap = performanceGap * 50; // 50x multiplier (was 40x) - EXTREME drama
            const minimumGap = performanceRanking > 0 ? 18 : 0; // Min 18% visual gap (was 12%) - at least one car length
            const dynamicGap = Math.min(Math.max(scaledGap, minimumGap), 45); // Max 45% separation (was 40%)!
            
            // Split the gap: leader goes UP, trailer goes DOWN from center
            // Leader: 50% + half gap (ahead)
            // Trailer: 50% - half gap (behind)
            const halfGap = dynamicGap / 2;
            const positionOffset = performanceRanking === 0 ? halfGap : -halfGap;
            
            // Leader at ~59-72.5%, Trailer at ~41-27.5%
            // HUGE visual separation - leader WAY ahead!
            verticalPosition = centerPosition + positionOffset;
          } else {
            // Fallback: idle at center (shouldn't reach here normally)
            const basePosition = 50; // Center position
            const revAmount = 1.5;
            const movement = Math.sin(animationTime * 0.8 + laneIndex) * revAmount;
            verticalPosition = basePosition + movement;
          }
          
          return (
            <RaceLane
              key={asset.symbol}
              asset={asset}
              laneIndex={laneIndex}
              totalLanes={race.assets.length}
              carPosition={verticalPosition}
              performance={assetWithPerformance}
              performanceRanking={performanceRanking}
              isMobile={isMobile}
              getAssetCSSColor={getAssetCSSColor}
              isBettingEnabled={isBettingEnabled}
              hasActiveBet={!!playerPick}
              bettingTimeRemaining={bettingTimeRemaining}
              currentPhase={currentRound?.phase}
              placementHistory={assetHistory}
              fullRaceHistory={fullRaceHistory}
              isVerticalLayout={true}
              betAmount={betAmount}
              tugOfWarBars={tugOfWarBars}
            />
          );
        })}
      </div>
    </div>
  );
};

export default RaceTrack;

